import type { User } from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';

type RegistrationMetadata = {
  full_name?: unknown;
  id_passport_number?: unknown;
  identity_document_type?: unknown;
  cell_number?: unknown;
};

const asText = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

/**
 * Email-confirmed signups do not get a browser session until the user signs in.
 * Preserve the initial registration values in Auth metadata, then fill only blank
 * profile fields on that first authenticated session.
 */
export const syncRegistrationProfile = async (user: Pick<User, 'id' | 'user_metadata'>) => {
  const metadata = (user.user_metadata ?? {}) as RegistrationMetadata;
  const identityDocumentType = asText(metadata.identity_document_type);
  const idPassportNumber = asText(metadata.id_passport_number);
  const cellNumber = asText(metadata.cell_number);
  const fullName = asText(metadata.full_name);

  if (
    !fullName &&
    !idPassportNumber &&
    !cellNumber &&
    !['sa_id', 'foreign_passport'].includes(identityDocumentType)
  ) {
    return;
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('full_name, id_passport_number, identity_document_type, cell_number')
    .eq('id', user.id)
    .maybeSingle();

  if (profileError) throw profileError;
  if (!profile) return;

  const patch: Record<string, string> = {};
  if (!asText(profile.full_name) && fullName) patch.full_name = fullName;
  if (!asText(profile.id_passport_number) && idPassportNumber) {
    patch.id_passport_number = idPassportNumber;
  }
  if (!asText(profile.identity_document_type) && ['sa_id', 'foreign_passport'].includes(identityDocumentType)) {
    patch.identity_document_type = identityDocumentType;
  }
  if (!asText(profile.cell_number) && cellNumber) patch.cell_number = cellNumber;

  if (Object.keys(patch).length === 0) return;

  const { error: updateError } = await supabase.from('profiles').update(patch).eq('id', user.id);
  if (updateError) throw updateError;
};
